import {err, ok} from 'framework';
import type {Result} from 'framework';
import {ImageFileValidator} from '@/shared/application/validators/image-file.validator';
import {
  HEADER_SCAN_BYTES,
  LLM_IMAGE_MAX_LONG_EDGE_PX,
  MAX_DECODE_PIXELS,
  MAX_INPUT_BYTES,
} from '../constants';
import {ImagePreprocessError} from '../image-preprocess.error';
import type {ImagePreprocessMessageContext} from '../image-preprocess.error';
import type {
  CropRegion,
  Dimensions,
  ExifOrientation,
  ImageContentHint,
  ImageContentKind,
  ImagePreprocessErrorCode,
  PreparedImage,
  PreprocessImageOptions,
  SniffedImage,
} from '../types';
import {chooseOutputLadder} from './choose-output-ladder';
import {classifierTiles, classifyImageContent, priorForSource} from './classify-image-content';
import {clampCropRegion, fitWithin} from './fit-within';
import {ImageCodecFailure} from './image-codec';
import type {ImageCodec, ImageCodecCapabilities} from './image-codec';
import {toUploadImageMimeType, withExtension} from './output-file-name';
import {inspectJpegHeader} from './inspect-jpeg-header';
import type {JpegHeaderScan} from './inspect-jpeg-header';
import {readImageHeader} from './read-image-header';
import {sniffImageFormat} from './sniff-image-format';

/** Options with every default applied; what crosses the worker boundary. */
export type PipelineOptions = {
  readonly constraints: PreprocessImageOptions['constraints'];
  readonly maxLongEdgePx: number;
  readonly contentHint: ImageContentHint;
  readonly crop: CropRegion | null;
  readonly signal?: AbortSignal;
};

export const normalizePreprocessOptions = (options: PreprocessImageOptions): PipelineOptions => ({
  constraints: options.constraints,
  maxLongEdgePx: options.maxLongEdgePx ?? LLM_IMAGE_MAX_LONG_EDGE_PX,
  contentHint: options.contentHint ?? 'auto',
  crop: options.crop ?? null,
  signal: options.signal,
});

type SourceFacts = {
  readonly sniff: SniffedImage;
  /** Stored (un-oriented) dimensions from the header; `null` when the container hides them. */
  readonly header: Dimensions | null;
  readonly orientation: ExifOrientation | null;
  /**
   * `false` when the JPEG header could not be read to its scan data, so a `null`
   * orientation means "not determined" rather than "absent"; such files are never
   * sent as-is.
   */
  readonly orientationKnown: boolean;
};

const FALLBACK_ELIGIBLE_CODES: ReadonlySet<ImagePreprocessErrorCode> = new Set([
  'decode-failed',
  'encode-failed',
  'output-too-large',
]);

const messageContext = (options: PipelineOptions): ImagePreprocessMessageContext => ({
  acceptedMimeTypes: options.constraints.acceptedMimeTypes,
  maxBytes: options.constraints.maxBytes,
});

const failure = (
  code: ImagePreprocessErrorCode,
  options: PipelineOptions,
  cause?: unknown,
): ImagePreprocessError => new ImagePreprocessError(code, messageContext(options), {cause});

const isAbortError = (cause: unknown): boolean =>
  cause instanceof Error && cause.name === 'AbortError';

const toPreprocessError = (cause: unknown, options: PipelineOptions): ImagePreprocessError => {
  if (cause instanceof ImagePreprocessError) {
    return cause;
  }
  if (cause instanceof ImageCodecFailure) {
    return failure(cause.code, options, cause);
  }
  if (isAbortError(cause)) {
    return failure('aborted', options, cause);
  }
  return failure('decode-failed', options, cause);
};

const throwIfAborted = (options: PipelineOptions): void => {
  if (options.signal?.aborted) {
    throw failure('aborted', options);
  }
};

const needsRotation = (orientation: ExifOrientation | null): orientation is ExifOrientation =>
  orientation !== null && orientation !== 1;

/** Same bytes, truthful `type` and extension; the original object when nothing changes. */
const relabel = (file: File, mimeType: string): File =>
  file.type === mimeType
    ? file
    : new File([file], withExtension(file.name, mimeType), {type: mimeType});

/**
 * An original that is already what the server wants goes up untouched: it is
 * an accepted format, within the byte cap and the long-edge cap, and carries no
 * rotation to bake. Re-encoding it would only cost a generation.
 */
const tryPassThrough = (
  file: File,
  facts: SourceFacts,
  options: PipelineOptions,
): PreparedImage | null => {
  const {sniff, header, orientation} = facts;
  const format = toUploadImageMimeType(sniff.mimeType);
  if (
    options.crop !== null ||
    format === null ||
    !options.constraints.acceptedMimeTypes.includes(format) ||
    file.size > options.constraints.maxBytes ||
    header === null ||
    Math.max(header.width, header.height) > options.maxLongEdgePx ||
    !facts.orientationKnown ||
    needsRotation(orientation) ||
    sniff.hasExifFlag
  ) {
    return null;
  }
  return {
    disposition: 'passed-through',
    file: relabel(file, format),
    format,
    kind: priorForSource(sniff),
    width: header.width,
    height: header.height,
  };
};

/**
 * After a failed pipeline the original may still go up, but only when the
 * server would accept it as-is: accepted format by bytes, header dimensions
 * known and within the decode cap, orientation determined, within the byte
 * cap. Anything else is refused with the pipeline's message.
 */
const resolveFallback = (
  file: File,
  facts: SourceFacts,
  options: PipelineOptions,
  error: ImagePreprocessError,
): PreparedImage | null => {
  if (!FALLBACK_ELIGIBLE_CODES.has(error.code)) {
    return null;
  }
  const format = toUploadImageMimeType(facts.sniff.mimeType);
  if (format === null || !options.constraints.acceptedMimeTypes.includes(format)) {
    return null;
  }
  if (
    facts.header === null ||
    facts.header.width * facts.header.height > MAX_DECODE_PIXELS ||
    !facts.orientationKnown
  ) {
    return null;
  }
  const validation = new ImageFileValidator(options.constraints).validate(relabel(file, format));
  if (!validation.ok) {
    return null;
  }
  return {disposition: 'fallback', file: validation.data, reason: error.code};
};

const fullFrame = (image: Dimensions): CropRegion => ({
  x: 0,
  y: 0,
  width: image.width,
  height: image.height,
});

const swapImage = async <TImage extends Dimensions>(
  codec: ImageCodec<TImage>,
  current: TImage,
  next: Promise<TImage>,
): Promise<TImage> => {
  const replacement = await next;
  if (replacement !== current) {
    codec.close(current);
  }
  return replacement;
};

const decideKind = <TImage extends Dimensions>(
  codec: ImageCodec<TImage>,
  image: TImage,
  facts: SourceFacts,
  options: PipelineOptions,
): ImageContentKind =>
  options.contentHint === 'auto'
    ? classifyImageContent(
        codec.samplePixels(image, classifierTiles(image)),
        priorForSource(facts.sniff),
      )
    : options.contentHint;

const processImage = async <TImage extends Dimensions>(
  file: File,
  facts: SourceFacts,
  options: PipelineOptions,
  codec: ImageCodec<TImage>,
  capabilities: ImageCodecCapabilities,
): Promise<PreparedImage> => {
  let image = await codec.decode(file, facts.sniff);
  try {
    throwIfAborted(options);
    if (needsRotation(facts.orientation) && !capabilities.appliesExifOrientation) {
      image = await swapImage(codec, image, codec.transform(image, facts.orientation));
    }
    const source: Dimensions = {width: image.width, height: image.height};
    if (facts.header === null && source.width * source.height > MAX_DECODE_PIXELS) {
      throw failure('too-many-pixels', options);
    }

    const region =
      (options.crop === null ? null : clampCropRegion(options.crop, source)) ?? fullFrame(source);
    const target = fitWithin(region, options.maxLongEdgePx);
    const cropped = region.width !== source.width || region.height !== source.height;
    if (target.scaled || cropped) {
      throwIfAborted(options);
      image = await swapImage(
        codec,
        image,
        codec.resize(image, region, {width: target.width, height: target.height}),
      );
    }

    throwIfAborted(options);
    const kind = decideKind(codec, image, facts, options);
    for (const step of chooseOutputLadder(kind)) {
      throwIfAborted(options);
      const blob = await codec.encode(image, step);
      if (blob.size <= options.constraints.maxBytes) {
        return {
          disposition: 'processed',
          file: new File([blob], withExtension(file.name, step.format), {type: step.format}),
          format: step.format,
          kind,
          width: image.width,
          height: image.height,
          sourceWidth: source.width,
          sourceHeight: source.height,
        };
      }
    }
    throw failure('output-too-large', options);
  } finally {
    codec.close(image);
  }
};

const readPrefix = async (file: File, length: number): Promise<Uint8Array | null> => {
  try {
    return new Uint8Array(await file.slice(0, length).arrayBuffer());
  } catch {
    return null;
  }
};

const SCAN_WINDOW_GROWTH = 4;

/**
 * Camera JPEGs may carry megabytes of ICC/MPF/XMP before the frame header.
 * Re-read in growing windows until the walk reaches the scan data (or the file
 * ends); the file is already bounded by `MAX_INPUT_BYTES`.
 */
const inspectJpegFile = async (file: File, head: Uint8Array): Promise<JpegHeaderScan | null> => {
  let scan = inspectJpegHeader(head);
  let window = head.length;
  while (scan.status === 'truncated' && window < file.size) {
    window = Math.min(window * SCAN_WINDOW_GROWTH, file.size);
    const bytes = await readPrefix(file, window);
    if (bytes === null) {
      return null;
    }
    scan = inspectJpegHeader(bytes);
  }
  return scan;
};

const collectFacts = async (
  file: File,
  head: Uint8Array,
  sniff: SniffedImage,
): Promise<SourceFacts | null> => {
  if (sniff.format !== 'jpeg') {
    return {
      sniff,
      header: readImageHeader(sniff.format, head),
      orientation: null,
      orientationKnown: true,
    };
  }
  const scan = await inspectJpegFile(file, head);
  if (scan === null || scan.frame === null) {
    return null;
  }
  return {
    sniff,
    header: scan.frame,
    orientation: scan.orientation,
    orientationKnown: scan.status === 'complete',
  };
};

/**
 * The shared core behind every upload: guards from the header (read past the
 * first window for JPEG when needed), pass-through when nothing needs doing,
 * otherwise decode once → orient → (crop) → resize → classify → encode once,
 * with a bounded fallback to the original.
 */
export const runPreprocessPipeline = async <TImage extends Dimensions>(
  file: File,
  options: PipelineOptions,
  codec: ImageCodec<TImage>,
  capabilities: ImageCodecCapabilities,
): Promise<Result<PreparedImage, ImagePreprocessError>> => {
  if (options.signal?.aborted) {
    return err(failure('aborted', options));
  }
  if (file.size > MAX_INPUT_BYTES) {
    return err(failure('input-too-large', options));
  }
  const head = await readPrefix(file, HEADER_SCAN_BYTES);
  if (head === null) {
    return err(failure('unreadable', options));
  }
  const sniff = sniffImageFormat(head);
  if (sniff === null) {
    return err(failure('unsupported-format', options));
  }
  // A JPEG without a readable frame header is never decoded: its size is unknown.
  const facts = await collectFacts(file, head, sniff);
  if (facts === null) {
    return err(failure('unreadable', options));
  }
  if (facts.header !== null && facts.header.width * facts.header.height > MAX_DECODE_PIXELS) {
    return err(failure('too-many-pixels', options));
  }

  const passedThrough = tryPassThrough(file, facts, options);
  if (passedThrough !== null) {
    return ok(passedThrough);
  }

  try {
    return ok(await processImage(file, facts, options, codec, capabilities));
  } catch (cause) {
    const error = toPreprocessError(cause, options);
    const fallback = resolveFallback(file, facts, options, error);
    return fallback === null ? err(error) : ok(fallback);
  }
};
